import React, { useState, useEffect, useCallback } from "react";
import { Tab, Tabs, Button, Modal, Form, Nav, Card, Row, Col } from "react-bootstrap";
import MaterialCategoryManager from "../MaterialCategoryManager/MaterialCategoryManager";
import axios from "axios";
import "./MaterialAndDependentManager.scss";
import MaterialCategorySelector from "./MaterialCategorySelector";
import MaterialRepairStyleSelector from "./MaterialRepairStyleSelector";
import { getApiEndpoint } from "../../../../utils/apiConfig";
import axiosWithAuth from "../../../../utils/axiosWithAuth";
import Swal from "sweetalert2";

// Store Selector Component
const MaterialStoreSelector = ({ stores, selectedStores, onChange }) => {
  if (!stores || stores.length === 0) {
    return <p>No stores available.</p>;
  }

  // Active stores only
  const activeStores = stores.filter(store => store.isActive !== false);

  const handleStoreChange = (e) => {
    const storeId = e.target.value;
    if (storeId && storeId !== "null") {
      // Check if store is already selected
      if (!selectedStores.some(sel => sel.id === storeId)) {
        onChange([...selectedStores, { id: storeId }]);
      }
    }
  };

  const removeStore = (storeId) => {
    const updatedStores = selectedStores.filter(sel => sel.id !== storeId);
    onChange(updatedStores.length > 0 ? updatedStores : [{ id: null }]);
  };

  // Normalize store IDs for comparison
  const normalizeId = (id) => {
    if (!id) return null;
    // If it's a MongoDB ObjectId as a string, it might be in the format { "$oid": "123..." }
    if (typeof id === 'object' && id.$oid) return id.$oid;
    // Otherwise just return the ID as is
    return id;
  };

  // Debug log
  console.log("MaterialStoreSelector - stores:", stores);
  console.log("MaterialStoreSelector - selectedStores:", selectedStores);

  return (
    <div className="material-store-selector">
      <Form.Group>
        <Form.Label>Select Stores</Form.Label>
        <Form.Control
          as="select"
          onChange={handleStoreChange}
          className="mb-2"
        >
          <option value="null">-- Select Store --</option>
          {activeStores.map(store => (
            <option key={store.id} value={store.id}>
              {store.name?.az || store.name?.text || "Unnamed Store"}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {/* Selected Stores */}
      <div className="selected-stores mt-2">
        <h6>Selected Stores:</h6>
        {selectedStores.filter(sel => sel.id).length === 0 ? (
          <p>No stores selected</p>
        ) : (
          <ul className="list-group">
            {selectedStores
              .filter(sel => sel.id)
              .map(sel => {
                // Try to find store using various ID formats
                const store = stores.find(c => {
                  const selId = normalizeId(sel.id);
                  const storeId = normalizeId(c.id);
                  return selId === storeId;
                });
                return (
                  <li key={sel.id} className="list-group-item d-flex justify-content-between align-items-center">
                    {store?.name?.az || store?.name?.text || "Unknown Store"}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeStore(sel.id)}
                    >
                      Remove
                    </Button>
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    </div>
  );
};

// Repair Tariff Selector Component
const MaterialRepairTariffSelector = ({ tariffs, selectedTariffs, onChange }) => {
  if (!tariffs || tariffs.length === 0) {
    return <p>No repair tariffs available.</p>;
  }

  // Active tariffs only
  const activeTariffs = tariffs.filter(tariff => tariff.status !== false);

  const handleTariffChange = (e) => {
    const tariffId = e.target.value;
    if (tariffId && tariffId !== "null") {
      // Check if tariff is already selected
      if (!selectedTariffs.some(sel => sel.id === tariffId)) {
        onChange([...selectedTariffs, { id: tariffId }]);
      }
    }
  };

  const removeTariff = (tariffId) => {
    const updatedTariffs = selectedTariffs.filter(sel => sel.id !== tariffId);
    onChange(updatedTariffs.length > 0 ? updatedTariffs : [{ id: null }]);
  };

  // Normalize tariff IDs for comparison
  const normalizeId = (id) => {
    if (!id) return null;
    // If it's a MongoDB ObjectId as a string, it might be in the format { "$oid": "123..." }
    if (typeof id === 'object' && id.$oid) return id.$oid;
    // Otherwise just return the ID as is
    return id;
  };

  // Debug log
  console.log("MaterialRepairTariffSelector - tariffs:", tariffs);
  console.log("MaterialRepairTariffSelector - selectedTariffs:", selectedTariffs);

  return (
    <div className="material-tariff-selector">
      <Form.Group>
        <Form.Label>Select Repair Tariffs</Form.Label>
        <Form.Control
          as="select"
          onChange={handleTariffChange}
          className="mb-2"
        >
          <option value="null">-- Select Repair Tariff --</option>
          {activeTariffs.map(tariff => (
            <option key={tariff.id} value={tariff.id}>
              {tariff.name?.az || tariff.name?.text || "Unnamed Tariff"}
            </option>
          ))}
        </Form.Control>
      </Form.Group>

      {/* Selected Tariffs */}
      <div className="selected-tariffs mt-2">
        <h6>Selected Repair Tariffs:</h6>
        {selectedTariffs.filter(sel => sel.id).length === 0 ? (
          <p>No repair tariffs selected</p>
        ) : (
          <ul className="list-group">
            {selectedTariffs
              .filter(sel => sel.id)
              .map(sel => {
                // Try to find tariff using various ID formats
                const tariff = tariffs.find(t => {
                  const selId = normalizeId(sel.id);
                  const tariffId = normalizeId(t.id);
                  return selId === tariffId;
                });
                return (
                  <li key={sel.id} className="list-group-item d-flex justify-content-between align-items-center">
                    {tariff?.name?.az || tariff?.name?.text || "Unknown Tariff"}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeTariff(sel.id)}
                    >
                      Remove
                    </Button>
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    </div>
  );
};

const MaterialAndDependentManager = () => {
  // Main state management
  const [activeTab, setActiveTab] = useState("dependentMaterials");
  const [loading, setLoading] = useState(false);
  
  // Categories, repair styles, tariffs, and stores management
  const [categories, setCategories] = useState([]);
  const [repairStyles, setRepairStyles] = useState([]);
  const [repairTariffs, setRepairTariffs] = useState([]);
  const [stores, setStores] = useState([]);
  
  // Selected items state variables
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedRepairStyles, setSelectedRepairStyles] = useState([]);
  const [selectedRepairTariffs, setSelectedRepairTariffs] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  
  // Material form fields
  const [materialName, setMaterialName] = useState({ az: "", en: "", ru: "" });
  const [materialDescription, setMaterialDescription] = useState({ az: "", en: "", ru: "" });
  const [materialUnitOfMeasure, setMaterialUnitOfMeasure] = useState("");
  const [materialMeasureCapacity, setMaterialMeasureCapacity] = useState("0");
  const [materialUsagePerSquareMeter, setMaterialUsagePerSquareMeter] = useState("0");
  const [materialPrice, setMaterialPrice] = useState("0");
  const [materialLowPrice, setMaterialLowPrice] = useState("0");
  const [materialMidPrice, setMaterialMidPrice] = useState("0");
  const [materialHighPrice, setMaterialHighPrice] = useState("0");
  const [materialServicePrice, setMaterialServicePrice] = useState("0");
  const [materialCurrency, setMaterialCurrency] = useState("AZN");
  const [materialIsSelectable, setMaterialIsSelectable] = useState(true);
  const [materialIsDefault, setMaterialIsDefault] = useState(false);
  const [materialIsActive, setMaterialIsActive] = useState(true);
  const [materialStatus, setMaterialStatus] = useState(true);
  const [materialImage, setMaterialImage] = useState(null);
  const [materialImageOnRoom, setMaterialImageOnRoom] = useState(null);
  
  // Dependent materials state
  const [dependentMaterials, setDependentMaterials] = useState([]);
  const [dependentMaterial, setDependentMaterial] = useState({
    name: { az: "", en: "", ru: "" },
    description: { az: "", en: "", ru: "" },
    categories: [], // This will now store category IDs directly as strings
    repairTariffs: [],
    stores: [],
    unitOfMeasure: "",
    measureCapacity: "",
    usagePerSquareMeter: "",
    price: "",
    servicePrice: "",
    currency: "AZN",
    image: { id: null },
    imageOnRoom: { id: null },
    isSelectable: true,
    isDefault: false,
    isActive: true,
    status: true,
    linkedDependentMaterials: []
  });
  const [editingDependentMaterial, setEditingDependentMaterial] = useState(false);
  const [dependentMaterialEditId, setDependentMaterialEditId] = useState(null);
  
  // Main materials state
  const [materials, setMaterials] = useState([]);
  const [material, setMaterial] = useState({
    name: { az: "", en: "", ru: "" },
    description: { az: "", en: "", ru: "" },
    categories: [],
    repairStyles: [],
    repairTariffs: [],
    stores: [],
    unitOfMeasure: "",
    measureCapacity: "",
    usagePerSquareMeter: "",
    price: "",
    servicePrice: "",
    currency: "AZN",
    image: { id: null },
    imageOnRoom: { id: null },
    isSelectable: true,
    isDefault: false,
    isActive: true,
    dependentMaterials: [],
    status: true,
  });
  const [editingMaterial, setEditingMaterial] = useState(false);
  const [materialEditId, setMaterialEditId] = useState(null);
  const [selectedDependentMaterials, setSelectedDependentMaterials] = useState([]);
  const [materialQuantities, setMaterialQuantities] = useState({});
  const [selectedLinkedMaterials, setSelectedLinkedMaterials] = useState([]);

  // Data loading operations
  useEffect(() => {
    fetchCategories();
    fetchRepairStyles();
    fetchRepairTariffs();
    fetchStores();
    fetchAllDependentMaterials();
    fetchAllMaterials();
  }, []);

  // Alias variables for selector components
  const availableCategories = categories;
  const availableRepairStyles = repairStyles;
  const availableRepairTariffs = repairTariffs;
  const availableStores = stores;

  // Fetch material categories
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosWithAuth();
      const response = await api.get("/material-categories/admin");
      // Kontrol edelim ve veriyi düzenleyelim
      console.log("Raw categories response:", response.data);
      
      // Yanıt yapısını kontrol et
      const categoryData = response.data?.data || response.data || [];
      const formattedCategories = Array.isArray(categoryData) ? categoryData
        .filter(cat => cat.status === true || cat.isActive === true) // Sadece aktif kategorileri filtrele
        .map(cat => ({
          id: cat._id || cat.id,
          name: cat.name,
          isActive: cat.isActive || cat.status
        })) : [];
      
      console.log("Fetched active categories:", formattedCategories);
      setCategories(formattedCategories);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      setCategories([]);
      Swal.fire({
        title: "Error",
        text: "There was a problem loading categories.",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch repair styles
  const fetchRepairStyles = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosWithAuth();
      const response = await api.get("/repair-styles/admin");
      // Kontrol edelim ve veriyi düzenleyelim
      console.log("Raw repair styles response:", response.data);
      
      // Yanıt yapısını kontrol et
      const styleData = response.data?.data || response.data || [];
      const formattedStyles = Array.isArray(styleData) ? styleData
        .filter(style => style.status === true || style.isActive === true) // Sadece aktif stilleri filtrele
        .map(style => ({
          id: style._id || style.id,
          name: style.name,
          isActive: style.isActive || style.status
        })) : [];
      
      console.log("Fetched active repair styles:", formattedStyles);
      setRepairStyles(formattedStyles);
    } catch (error) {
      console.error("Failed to fetch repair styles:", error);
      setRepairStyles([]);
      Swal.fire({
        title: "Error",
        text: "There was a problem loading repair styles.",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch repair tariffs
  const fetchRepairTariffs = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosWithAuth();
      const response = await api.get("/repair-tariffs/admin");
      // Kontrol edelim ve veriyi düzenleyelim
      console.log("Raw repair tariffs response:", response.data);
      
      // Yanıt yapısını kontrol et
      const tariffData = response.data?.data || response.data || [];
      const formattedTariffs = Array.isArray(tariffData) ? tariffData
        .filter(tariff => tariff.status === true) // Sadece aktif tarifeleri filtrele
        .map(tariff => ({
          id: tariff._id || tariff.id,
          name: tariff.name,
          status: tariff.status
        })) : [];
      
      console.log("Fetched active repair tariffs:", formattedTariffs);
      setRepairTariffs(formattedTariffs);
    } catch (error) {
      console.error("Failed to fetch repair tariffs:", error);
      setRepairTariffs([]);
      Swal.fire({
        title: "Error",
        text: "There was a problem loading repair tariffs.",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch stores
  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosWithAuth();
      const response = await api.get("/stores");
      console.log("Raw stores response:", response.data);
      
      // Yanıt yapısını kontrol et
      const storeData = response.data?.data || response.data || [];
      const formattedStores = Array.isArray(storeData) ? storeData
        .filter(store => store.status === true || store.isActive === true) // Sadece aktif mağazaları filtrele
        .map(store => ({
          id: store._id || store.id,
          name: store.name,
          isActive: store.isActive || store.status
        })) : [];
      
      console.log("Fetched active stores:", formattedStores);
      setStores(formattedStores);
    } catch (error) {
      console.error("Failed to fetch stores:", error);
      setStores([]);
      Swal.fire({
        title: "Error",
        text: "There was a problem loading stores.",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Bir mono file ID'si için direkt olarak bilgi çekmek için fonksiyon
  const fetchMonoFileDetails = async (fileId) => {
    if (!fileId) return null;
    
    try {
      const api = axiosWithAuth();
      const response = await api.get(`/mono-files/${fileId}`);
      console.log(`Fetched mono-file ${fileId} details:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch mono-file details for ${fileId}:`, error);
      return null;
    }
  };
  
  // Fetch all dependent materials
  const fetchAllDependentMaterials = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosWithAuth();
      const response = await api.get("/dependent-materials/admin");
      
      console.log("fetchAllDependentMaterials response:", response.data);
      
      // Clear existing data first
      setDependentMaterials([]);
      
      // Process and set new data
      if (response.data && Array.isArray(response.data)) {
        const transformedMaterials = [];
        
        for (const material of response.data) {
          console.log("Processing material:", material);
          
          const transformedMaterial = {
            ...material,
            id: material._id || material.id,
            name: material.name?.text ? {
              id: material.name?.id,
              text: material.name?.text || "",
              az: material.name?.az || material.name?.text || "",
              en: material.name?.en || material.name?.text || "",
              ru: material.name?.ru || material.name?.text || ""
            } : material.name || { text: "", az: "", en: "", ru: "" },
            description: material.description?.text ? {
              id: material.description?.id,
              text: material.description?.text || "",
              az: material.description?.az || material.description?.text || "",
              en: material.description?.en || material.description?.text || "",
              ru: material.description?.ru || material.description?.text || ""
            } : material.description || { text: "", az: "", en: "", ru: "" },
            categories: material.categories?.map(cat => ({
              ...cat,
              name: cat.name?.text ? {
                text: cat.name?.text || "",
                az: cat.name?.az || cat.name?.text || "",
                en: cat.name?.en || cat.name?.text || "",
                ru: cat.name?.ru || cat.name?.text || ""
              } : cat.name || { text: "", az: "", en: "", ru: "" }
            })) || [],
            repairTariffs: material.repairTariffs?.map(tariff => ({
              ...tariff,
              name: tariff.name?.text ? {
                text: tariff.name?.text || "",
                az: tariff.name?.az || tariff.name?.text || "",
                en: tariff.name?.en || tariff.name?.text || "",
                ru: tariff.name?.ru || tariff.name?.text || ""
              } : tariff.name || { text: "", az: "", en: "", ru: "" }
            })) || [],
            stores: material.stores?.map(store => ({
              ...store,
              name: store.name?.text ? {
                text: store.name?.text || "",
                az: store.name?.az || store.name?.text || "",
                en: store.name?.en || store.name?.text || "",
                ru: store.name?.ru || store.name?.text || ""
              } : store.name || { text: "", az: "", en: "", ru: "" }
            })) || [],
            image: material.image || null,
            imageOnRoom: material.imageOnRoom || null,
            isSelectable: !!material.isSelectable,
            isDefault: !!material.isDefault,
            isActive: !!material.isActive,
            status: !!material.status
          };
          
          console.log("Transformed material:", transformedMaterial);
          transformedMaterials.push(transformedMaterial);
        }
        
        console.log("Setting transformed materials:", transformedMaterials);
        setDependentMaterials(transformedMaterials);
      } else {
        console.warn("Unexpected API response format:", response.data);
        setDependentMaterials([]);
      }
    } catch (error) {
      console.error("Failed to fetch dependent materials:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
      }
      setDependentMaterials([]);
      Swal.fire({
        title: "Error",
        text: "There was a problem loading dependent materials.",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch all materials
  const fetchAllMaterials = useCallback(() => {
    setLoading(true);
    fetchMaterials();
  }, []);
  
  // Fetch materials function
  const fetchMaterials = async () => {
    try {
    const api = axiosWithAuth();
      const response = await api.get("/materials?strictPopulate=false");
      console.log("Fetched materials:", response.data);
      
      const materialsData = response.data?.data || response.data || [];
      setMaterials(Array.isArray(materialsData) ? materialsData : []);
    } catch (error) {
        console.error("Failed to fetch materials:", error);
        setMaterials([]);
        Swal.fire({
          title: "Error",
          text: "There was a problem loading materials.",
          icon: "error",
          timer: 2000,
        });
    } finally {
      setLoading(false);
    }
  };

  // Image upload
  const uploadImage = async (file) => {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      setLoading(true);
      const api = axiosWithAuth();
      const response = await api.post("/mono-files/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setLoading(false);
      return response.data.id;
    } catch (error) {
      console.error("Failed to upload image:", error);
      setLoading(false);
      Swal.fire({
        title: "Error",
        text: "There was a problem uploading the image.",
        icon: "error",
        timer: 2000,
      });
      return null;
    }
  };

  // Initialize object handlers
  const initializeMultiLanguageObject = (obj) => {
    if (!obj || typeof obj !== 'object') return { az: "", en: "", ru: "" };
    return {
      az: obj.az || "",
      en: obj.en || "",
      ru: obj.ru || ""
    };
  };

  // Form input handling - multi-language support
  const handleMultiLangInput = (e, stateSetter, currentState) => {
    const { name, value, dataset } = e.target;
    const field = dataset.field;
    
    if (["az", "en", "ru"].includes(name)) {
      const currentFieldValue = currentState[field] || { az: "", en: "", ru: "" };
      stateSetter({
        ...currentState,
        [field]: { ...currentFieldValue, [name]: value },
      });
    } else {
      stateSetter({
        ...currentState,
        [name]: value,
      });
    }
  };

  // Process numeric values
  const handleNumericInput = (e, stateSetter, currentState) => {
    const { name, value } = e.target;
    const numericFields = [
      "measureCapacity",
      "usagePerSquareMeter",
      "price", // Yeni tek fiyat alanı
      "lowPrice",
      "midPrice",
      "highPrice",
      "servicePrice",
    ];
    
    stateSetter({
      ...currentState,
      [name]: numericFields.includes(name) ? parseFloat(value) || 0 : value,
    });
  };

  // Process dropdown changes
  const handleDropdownChange = (e, field, stateSetter, currentState) => {
    const value = e.target.value;
    
    // Boş değer kontrolü
    if (value === "") {
      stateSetter({
        ...currentState,
        [field]: [{ id: null }]
      });
      return;
    }
    
    // ID'ler MongoDB ObjectId olarak string formatında, 
    // Number'a çevirmeye gerek yok, direkt olarak kullanabiliriz
    console.log(`Dropdown change - field: ${field}, value: ${value}`);
    
    stateSetter({
      ...currentState,
      [field]: [{ id: value }]
    });
  };

  // Process image upload
  const handleImageUpload = async (e, type, stateSetter, currentState) => {
    const file = e.target.files[0];
    if (!file) {
      console.log(`No file selected for ${type}`);
      return;
    }
    
    console.log(`Uploading ${type} file:`, file.name, file.size, file.type);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      setLoading(true);
      const api = axiosWithAuth();
      const response = await api.post("/mono-files/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      console.log(`${type} upload response:`, response.data);
      
      if (response.data && response.data.id) {
        const imageId = response.data.id;
        console.log(`Successfully uploaded ${type}, got ID:`, imageId);
        
        stateSetter({
          ...currentState,
          [type]: { 
            ...currentState[type], 
            id: imageId,
            path: response.data.path || null,
            fileName: response.data.fileName || null,
            mimeType: response.data.mimeType || file.type
          },
        });
        
        Swal.fire({
          title: "Success",
          text: `${type} uploaded successfully!`,
          icon: "success",
          timer: 1500,
        });
      } else {
        console.error(`No ID returned from ${type} upload:`, response.data);
        Swal.fire({
          title: "Warning",
          text: `${type} upload successful but no ID returned.`,
          icon: "warning",
          timer: 2000,
        });
      }
    } catch (error) {
      console.error(`Failed to upload ${type}:`, error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
      }
      Swal.fire({
        title: "Error",
        text: `There was a problem uploading the ${type}.`,
        icon: "error",
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Toggle dependent material selection
  const toggleDependentMaterial = (depMaterial) => {
    console.log("Toggling dependent material:", depMaterial);
    
    // Update selected dependent materials list
    const isSelected = selectedDependentMaterials.some(item => item.id === depMaterial.id);
    
    if (isSelected) {
      // If already selected, remove it
      setSelectedDependentMaterials(prev => prev.filter(item => item.id !== depMaterial.id));
      setMaterialQuantities(prev => {
        const newQuantities = { ...prev };
        delete newQuantities[depMaterial.id];
        return newQuantities;
      });
      
      // Update material object by removing this dependent material
      setMaterial(prev => ({
        ...prev,
        linkedDependentMaterials: prev.linkedDependentMaterials?.filter(dm => dm.material !== depMaterial.id) || []
      }));
      
      console.log("Removed dependent material:", depMaterial.id);
    } else {
      // If not selected, add it with default quantity of 1
      setSelectedDependentMaterials(prev => [...prev, depMaterial]);
      setMaterialQuantities(prev => ({
        ...prev,
        [depMaterial.id]: 1
      }));
      
      // Update material object by adding this dependent material
      const updatedLinkedMaterials = [
        ...(material.linkedDependentMaterials || []).filter(dm => dm.material !== depMaterial.id),
        { material: depMaterial.id, quantity: 1 }
      ];
      
      setMaterial(prev => ({
        ...prev,
        linkedDependentMaterials: updatedLinkedMaterials
      }));
      
      console.log("Added dependent material with quantity 1");
    }
  };

  const handleQuantityChange = (materialId, quantity) => {
    // Update quantities state
    setMaterialQuantities(prev => ({
      ...prev,
      [materialId]: quantity
    }));
    
    // Update material object
    setMaterial(prev => ({
      ...prev,
      linkedDependentMaterials: prev.linkedDependentMaterials?.map(dm => 
        dm.material === materialId ? { ...dm, quantity } : dm
      ) || []
    }));
  };

  // Reset forms
  const resetDependentMaterialForm = () => {
    setDependentMaterial({
      name: { az: "", en: "", ru: "" },
      description: { az: "", en: "", ru: "" },
      categories: [], // Reset with empty array for category IDs
      repairTariffs: [],
      stores: [],
      unitOfMeasure: "",
      measureCapacity: "",
      usagePerSquareMeter: "",
      price: "",
      servicePrice: "",
      currency: "AZN",
      image: { id: null },
      imageOnRoom: { id: null },
      isSelectable: true,
      isDefault: false,
      isActive: true,
      status: true,
      linkedDependentMaterials: []
    });
    setEditingDependentMaterial(false);
    setDependentMaterialEditId(null);
  };

  const resetMaterialForm = () => {
    setMaterial({
      name: { az: "", en: "", ru: "" },
      description: { az: "", en: "", ru: "" },
      categories: [],
      repairStyles: [],
      repairTariffs: [],
      stores: [],
      unitOfMeasure: "",
      measureCapacity: "",
      usagePerSquareMeter: "",
      price: "",
      servicePrice: "",
      currency: "AZN",
      image: { id: null },
      imageOnRoom: { id: null },
      isSelectable: true,
      isDefault: false,
      isActive: true,
      dependentMaterials: [],
      status: true,
    });
    setEditingMaterial(false);
    setMaterialEditId(null);
    setSelectedDependentMaterials([]);
  };

  // Backend API formatına dönüştürme fonksiyonu - Material için farklı
  const transformToApiFormat = (data, type = 'dependent') => {
    console.log("Starting transformToApiFormat with data:", data);
    
    // İsim ve açıklama alanlarını dönüştür
    const nameTransformed = data.name ? {
      ...data.name,
      text: data.name.az || ""
    } : { text: "" };
    
    const descriptionTransformed = data.description ? {
      ...data.description,
      text: data.description.az || ""
    } : { text: "" };
    
    // Kategori ve tamir stilleri için doğru format
    const categoriesTransformed = (data.categories || []).map(cat => ({
      id: typeof cat === 'object' ? (cat.id || cat._id) : cat
    }));

    console.log("Final transformed categories:", categoriesTransformed);
    
    // Linked dependent materials için doğru format
    const linkedDependentMaterialsTransformed = data.linkedDependentMaterials?.map(link => ({
      material: link.material,
      quantity: parseInt(link.quantity) || 1
    })) || [];
    
    // API'nin beklediği formata dönüştür
    const baseTransform = {
      name: nameTransformed,
      description: descriptionTransformed,
      categories: categoriesTransformed,
      repairTariffs: (data.repairTariffs || [])
        .filter(t => t && (t.id || typeof t === 'string'))
        .map(t => ({ id: typeof t === 'string' ? t : (t.id || t._id) })),
      stores: (data.stores || [])
        .filter(s => s && (s.id || typeof s === 'string'))
        .map(s => ({ id: typeof s === 'string' ? s : (s.id || s._id) })),
      unitOfMeasure: data.unitOfMeasure || "",
      measureCapacity: parseFloat(data.measureCapacity) || 0,
      usagePerSquareMeter: parseFloat(data.usagePerSquareMeter) || 0,
      price: parseFloat(data.price) || 0,
      servicePrice: parseFloat(data.servicePrice) || 0,
      currency: data.currency || "AZN",
      isSelectable: Boolean(data.isSelectable),
      isDefault: Boolean(data.isDefault),
      isActive: Boolean(data.isActive),
      status: Boolean(data.status),
      linkedDependentMaterials: linkedDependentMaterialsTransformed,
      image: data.image?.id || null,
      imageOnRoom: data.imageOnRoom?.id || null
    };

    return baseTransform;
  };

  // Handle material submission
  const handleMaterialSubmit = (e) => {
    e.preventDefault();
    // Validate ve debug log ekleyelim
    console.log("Form submit edildi - materyal değerleri:", material);
    console.log("Kategoriler:", material.categories);
    console.log("Tamir stilleri:", material.repairStyles);
    console.log("Bağımlı materyaller:", material.dependentMaterials);
    
    // Detailed validation check
    let validationErrors = [];
    
    // Category and repair style validation
    if (!material.categories?.length) {
      validationErrors.push("Category not selected");
    }
    
    if (!material.repairStyles?.length) {
      validationErrors.push("Repair style not selected");
    }
    
    // Repair tariff validation
    if (!material.repairTariffs?.length) {
      validationErrors.push("Repair tariff not selected");
    }
    
    // Name and description validation
    if (!material.name?.az?.trim()) validationErrors.push("Name (AZ) cannot be empty");
    if (!material.name?.en?.trim()) validationErrors.push("Name (EN) cannot be empty");
    if (!material.name?.ru?.trim()) validationErrors.push("Name (RU) cannot be empty");
    
    if (!material.description?.az?.trim()) validationErrors.push("Description (AZ) cannot be empty");
    if (!material.description?.en?.trim()) validationErrors.push("Description (EN) cannot be empty");
    if (!material.description?.ru?.trim()) validationErrors.push("Description (RU) cannot be empty");
    
    // Unit validation
    if (!material.unitOfMeasure) validationErrors.push("Unit of measure not selected");
    
    // Show validation errors if any
    if (validationErrors.length > 0) {
      console.log("Validation errors:", validationErrors);
      Swal.fire({
        title: "Warning!",
        html: `Please fill in the following fields:<br><ul>${validationErrors.map(err => `<li>${err}</li>`).join('')}</ul>`,
        icon: "warning",
      });
      return;
    }
    
    try {
      // API için veri hazırlama - transformToApiFormat kullan
      const apiData = transformToApiFormat(material, 'material');

      // Temizlenmiş veriyi logla
      console.log("API'ye gönderilecek temizlenmiş veri:", apiData);

      // Store bilgisini kontrol edip konsola yazdıralım
      console.log("Gönderilen store bilgisi:", material.stores);
      console.log("Gönderilen bağımlı materyal bilgisi:", apiData.dependentMaterials);

      if (materialImage) {
        apiData.image = materialImage._id;
      }

      if (materialImageOnRoom) {
        apiData.imageOnRoom = materialImageOnRoom._id;
      }
      
      const api = axiosWithAuth();
      
      if (editingMaterial) {
        // Mevcut materyal güncelleme - doğru endpoint kullan
        api.put(`/materials/${editingMaterial.id}`, {
          ...apiData,
          categories: (apiData.categories || []).map(c => typeof c === "object" ? (c.id || c._id) : c).filter(Boolean),
          repairStyles: (apiData.repairStyles || []).map(s => typeof s === "object" ? (s.id || s._id) : s).filter(Boolean),
          repairTariffs: (apiData.repairTariffs || []).map(t => typeof t === "object" ? (t.id || t._id) : t).filter(Boolean),
          stores: (apiData.stores || []).map(s => typeof s === "object" ? (s.id || s._id) : s).filter(Boolean),
          linkedDependentMaterials: apiData.linkedDependentMaterials || []
        })
          .then((res) => {
        Swal.fire({
              title: "Success!",
              text: "Material updated successfully",
              icon: "success",
            });
            resetMaterialForm();
            fetchMaterials();
          })
          .catch((err) => {
            console.error("Error while updating material:", err);
            // Show API error response
            if (err.response) {
              console.log("API error response:", err.response.data);
            }
            Swal.fire({
              title: "Error!",
              text: "An error occurred while updating the material",
              icon: "error",
            });
        });
      } else {
        // Yeni materyal oluşturma - doğru endpoint kullan
        api.post("/materials", {
          ...apiData,
          categories: (apiData.categories || []).map(c => typeof c === "object" ? (c.id || c._id) : c).filter(Boolean),
          repairStyles: (apiData.repairStyles || []).map(s => typeof s === "object" ? (s.id || s._id) : s).filter(Boolean),
          repairTariffs: (apiData.repairTariffs || []).map(t => typeof t === "object" ? (t.id || t._id) : t).filter(Boolean),
          stores: (apiData.stores || []).map(s => typeof s === "object" ? (s.id || s._id) : s).filter(Boolean),
          linkedDependentMaterials: apiData.linkedDependentMaterials || []
        })
          .then((res) => {
        Swal.fire({
              title: "Success!",
              text: "Material created successfully",
              icon: "success",
            });
            resetMaterialForm();
            fetchMaterials();
          })
          .catch((err) => {
            console.error("Error while creating material:", err);
            // Show API error response
            if (err.response) {
              console.log("API error response:", err.response.data);
            }
            Swal.fire({
              title: "Error!",
              text: "An error occurred while creating the material",
              icon: "error",
            });
          });
      }
    } catch (error) {
      console.error("Process error:", error);
      Swal.fire({
        title: "Error!",
        text: "An error occurred during the operation",
        icon: "error",
      });
    }
  };

  // Handle dependent material submission
  const handleDependentMaterialSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      console.log("Form submission - Raw dependent material data:", dependentMaterial);
      console.log("Selected categories before transformation:", dependentMaterial.categories);
      
      // API verisi oluştur
      const apiData = transformToApiFormat(dependentMaterial, 'dependent');
      
      console.log("Transformed API data:", apiData);
      console.log("Categories after transformation:", apiData.categories);

      const api = axiosWithAuth();
      
      if (editingDependentMaterial) {
        console.log("Updating dependent material with ID:", dependentMaterialEditId);
        console.log("Update payload:", apiData);
        
        // Edit existing dependent material
        await api.put(`/dependent-materials/${dependentMaterialEditId}`, {
          ...apiData,
          categories: (apiData.categories || []).map(c => typeof c === "object" ? { id: c.id || c._id } : { id: c }).filter(c => c.id),
          repairTariffs: (apiData.repairTariffs || []).map(t => typeof t === "object" ? { id: t.id || t._id } : { id: t }).filter(t => t.id),
          stores: (apiData.stores || []).map(s => typeof s === "object" ? { id: s.id || s._id } : { id: s }).filter(s => s.id)
        });
        Swal.fire({
          title: "Success",
          text: "Dependent material updated successfully!",
          icon: "success",
          timer: 2000,
        });
      } else {
        // Create new dependent material
        await api.post("/dependent-materials", {
          ...apiData,
          categories: (apiData.categories || []).map(c => typeof c === "object" ? { id: c.id || c._id } : { id: c }).filter(c => c.id),
          repairTariffs: (apiData.repairTariffs || []).map(t => typeof t === "object" ? { id: t.id || t._id } : { id: t }).filter(t => t.id),
          stores: (apiData.stores || []).map(s => typeof s === "object" ? { id: s.id || s._id } : { id: s }).filter(s => s.id)
        });
        Swal.fire({
          title: "Success",
          text: "Dependent material created successfully!",
          icon: "success",
          timer: 2000,
        });
      }
      
      // Reset form and refresh data
      setDependentMaterial({
        name: { az: "", en: "", ru: "" },
        description: { az: "", en: "", ru: "" },
        categories: [], // Reset with empty array for category IDs
        repairTariffs: [],
        stores: [],
        unitOfMeasure: "",
        measureCapacity: "",
        usagePerSquareMeter: "",
        price: "",
        servicePrice: "",
        currency: "AZN",
        image: { id: null },
        imageOnRoom: { id: null },
        isSelectable: true,
        isDefault: false,
        isActive: true,
        status: true,
        linkedDependentMaterials: []
      });
      setEditingDependentMaterial(false);
      setDependentMaterialEditId(null);
      
      fetchAllDependentMaterials();
    } catch (error) {
      console.error("Failed to submit dependent material:", error);
      // API yanıtını loglayalım
      if (error.response) {
        console.log("Error response data:", error.response.data);
      }
      Swal.fire({
        title: "Error",
        text: "Failed to submit dependent material. Please try again.",
        icon: "error",
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle edit dependent material
  const handleEditDependentMaterial = (item) => {
    console.log("Editing dependent material - Raw data:", item);
    
    // MongoDB formatını frontend formatına dönüştür
    const normalizedItem = convertMongoObjectToFrontendFormat(item);
    console.log("Normalized item data:", normalizedItem);
    
    // Process linked materials - handle the API response format
    const linkedMaterials = normalizedItem.linkedDependentMaterials?.map(link => {
      // In the API response, the ID is directly in the link object
      const materialId = link.id || (typeof link.material === 'object' 
        ? link.material._id || link.material.id 
        : link.material);

      console.log("Processing linked material:", {
        original: link,
        extractedId: materialId
      });

      if (!materialId) {
        console.warn("Could not extract material ID from link:", link);
      }

      return {
        material: materialId, // This is the key field needed for the API
        quantity: link.quantity || 1
      };
    }) || [];

    console.log("Processed linked materials:", linkedMaterials);

    // Form state'ini güncelle
    const updatedDependentMaterial = {
      id: normalizedItem.id,
      categories: normalizedItem.categories?.map(cat => 
        typeof cat === 'object' ? cat.id || cat._id : cat
      ) || [],
      repairTariffs: normalizedItem.repairTariffs?.map(tariff => 
        typeof tariff === 'object' ? tariff.id || tariff._id : tariff
      ) || [],
      stores: normalizedItem.stores?.map(store => 
        typeof store === 'object' ? store.id || store._id : store
      ) || [],
      linkedDependentMaterials: linkedMaterials,
      name: normalizedItem.name || { az: "", en: "", ru: "" },
      description: normalizedItem.description || { az: "", en: "", ru: "" },
      unitOfMeasure: normalizedItem.unitOfMeasure || "",
      measureCapacity: normalizedItem.measureCapacity || 0,
      usagePerSquareMeter: normalizedItem.usagePerSquareMeter || 0,
      price: normalizedItem.price || 0,
      servicePrice: normalizedItem.servicePrice || 0,
      currency: normalizedItem.currency || "AZN",
      isSelectable: normalizedItem.isSelectable ?? true,
      isDefault: normalizedItem.isDefault ?? false,
      isActive: normalizedItem.isActive ?? true,
      status: normalizedItem.status ?? true,
    };

    console.log("Updated form state:", updatedDependentMaterial);
    setDependentMaterial(updatedDependentMaterial);
    setDependentMaterialEditId(normalizedItem.id);
    setEditingDependentMaterial(true);
  };

  // Handle edit material
  const handleEditMaterial = (materialData) => {
    console.log("Material to edit:", materialData);
    
    // Normalize edelim
    const normalizedMaterial = convertMongoObjectToFrontendFormat(materialData);
    console.log("Normalize edilmiş materyal:", normalizedMaterial);
    
    // Debug name ve description
    console.log("Name değerleri:", normalizedMaterial.name);
    console.log("Description değerleri:", normalizedMaterial.description);
    
    // İsim ve açıklama alanlarını doğru formatta oluştur
    let nameObj = { az: "", en: "", ru: "" };
    let descObj = { az: "", en: "", ru: "" };
    
    // Name formatlaması
    if (normalizedMaterial.name) {
      if (typeof normalizedMaterial.name === 'object') {
        // Direkt olarak az, en, ru değerleri varsa
        if (normalizedMaterial.name.az || normalizedMaterial.name.en || normalizedMaterial.name.ru) {
          nameObj = {
            az: normalizedMaterial.name.az || "",
            en: normalizedMaterial.name.en || "",
            ru: normalizedMaterial.name.ru || ""
          };
        } 
        // Text değeri varsa ama dil değerleri yoksa
        else if (normalizedMaterial.name.text) {
          nameObj = {
            az: normalizedMaterial.name.text || "",
            en: normalizedMaterial.name.text || "",
            ru: normalizedMaterial.name.text || ""
          };
        }
      } 
      // String bir değerse
      else if (typeof normalizedMaterial.name === 'string') {
        nameObj = {
          az: normalizedMaterial.name,
          en: normalizedMaterial.name,
          ru: normalizedMaterial.name
        };
      }
    }
    
    // Description formatlaması
    if (normalizedMaterial.description) {
      if (typeof normalizedMaterial.description === 'object') {
        // Direkt olarak az, en, ru değerleri varsa
        if (normalizedMaterial.description.az || normalizedMaterial.description.en || normalizedMaterial.description.ru) {
          descObj = {
            az: normalizedMaterial.description.az || "",
            en: normalizedMaterial.description.en || "",
            ru: normalizedMaterial.description.ru || ""
          };
        } 
        // Text değeri varsa ama dil değerleri yoksa
        else if (normalizedMaterial.description.text) {
          descObj = {
            az: normalizedMaterial.description.text || "",
            en: normalizedMaterial.description.text || "",
            ru: normalizedMaterial.description.text || ""
          };
        }
      } 
      // String bir değerse
      else if (typeof normalizedMaterial.description === 'string') {
        descObj = {
          az: normalizedMaterial.description,
          en: normalizedMaterial.description,
          ru: normalizedMaterial.description
        };
      }
    }
    
    console.log("Formatlanmış name:", nameObj);
    console.log("Formatlanmış description:", descObj);
    
    // Kategori ID'lerini düzgün şekilde ayarla
    const categoryIds = normalizedMaterial.categories?.map(cat => 
      typeof cat === 'object' ? cat.id || cat._id : cat
    ) || [];
    
    const selectedCats = availableCategories.filter(cat => 
      categoryIds.includes(cat.id)
    ).map(cat => ({id: cat.id}));
    
    // Tamir stilleri ID'lerini düzgün şekilde ayarla
    const styleIds = normalizedMaterial.repairStyles?.map(style => 
      typeof style === 'object' ? style.id || style._id : style
    ) || [];
    
    const selectedStyles = availableRepairStyles.filter(style => 
      styleIds.includes(style.id)
    ).map(style => ({id: style.id}));
    
    // Tarif ID'lerini düzgün şekilde ayarla
    const tariffIds = normalizedMaterial.repairTariffs?.map(tariff => 
      typeof tariff === 'object' ? tariff.id || tariff._id : tariff
    ) || [];
    
    const selectedTariffs = availableRepairTariffs.filter(tariff => 
      tariffIds.includes(tariff.id)
    ).map(tariff => ({id: tariff.id}));
    
    // Store ID'lerini düzgün şekilde ayarla
    const storeIds = normalizedMaterial.stores?.map(store => 
      typeof store === 'object' ? store.id || store._id : store
    ) || [];
    
    const selectedStores = availableStores.filter(store => 
      storeIds.includes(store.id)
    ).map(store => ({id: store.id}));
    
    // İmaj verilerini düzgün formata getir
    const imageData = normalizedMaterial.image ? {
      id: typeof normalizedMaterial.image === 'object' 
        ? normalizedMaterial.image.id || normalizedMaterial.image._id 
        : normalizedMaterial.image,
      url: typeof normalizedMaterial.image === 'object' ? normalizedMaterial.image.url : null
    } : null;
    
    const imageOnRoomData = normalizedMaterial.imageOnRoom ? {
      id: typeof normalizedMaterial.imageOnRoom === 'object' 
        ? normalizedMaterial.imageOnRoom.id || normalizedMaterial.imageOnRoom._id 
        : normalizedMaterial.imageOnRoom,
      url: typeof normalizedMaterial.imageOnRoom === 'object' ? normalizedMaterial.imageOnRoom.url : null
    } : null;

    // Bağımlı materyalleri düzgün formata getir
    const dependentMaterialsData = normalizedMaterial.dependentMaterials?.map(dep => {
      if (!dep) return null;
      
      // ID'yi al
      const depId = typeof dep === 'object' ? dep.id || dep._id : dep;
      
      // Eğer ID yoksa null dön
      if (!depId) return null;
      
      return {
        id: depId,
        name: dep.name || {},
        description: dep.description || {},
        // Diğer gerekli alanları ekle
      };
    }).filter(Boolean) || [];

    console.log("Dependent materials data:", dependentMaterialsData);
    
    // Materyal nesnesini güncelle
    const updatedMaterial = {
      name: nameObj,
      description: descObj,
      categories: selectedCats,
      repairStyles: selectedStyles,
      repairTariffs: selectedTariffs,
      stores: selectedStores,
      unitOfMeasure: normalizedMaterial.unitOfMeasure || "",
      measureCapacity: normalizedMaterial.measureCapacity?.toString() || "0",
      usagePerSquareMeter: normalizedMaterial.usagePerSquareMeter?.toString() || "0",
      price: normalizedMaterial.price?.toString() || "0",
      lowPrice: normalizedMaterial.lowPrice?.toString() || "0",
      midPrice: normalizedMaterial.midPrice?.toString() || "0",
      highPrice: normalizedMaterial.highPrice?.toString() || "0",
      servicePrice: normalizedMaterial.servicePrice?.toString() || "0",
      currency: normalizedMaterial.currency || "AZN",
      image: imageData,
      imageOnRoom: imageOnRoomData,
      isSelectable: normalizedMaterial.isSelectable === undefined ? true : normalizedMaterial.isSelectable,
      isDefault: normalizedMaterial.isDefault === undefined ? false : normalizedMaterial.isDefault,
      isActive: normalizedMaterial.isActive === undefined ? true : normalizedMaterial.isActive,
      status: normalizedMaterial.status === undefined ? true : normalizedMaterial.status,
      dependentMaterials: dependentMaterialsData
    };

    console.log("Setting material state with:", updatedMaterial);
    setMaterial(updatedMaterial);
    
    // Düzenleme modunu aktifleştir
    setEditingMaterial(normalizedMaterial);
    setMaterialEditId(normalizedMaterial.id || normalizedMaterial._id);
    
    // Seçili kategorileri ve tamir stillerini de güncelle
    setSelectedCategories(selectedCats);
    setSelectedRepairStyles(selectedStyles);
    setSelectedRepairTariffs(selectedTariffs);
    setSelectedStores(selectedStores);
    
    // Form alanına geçiş
    setActiveTab("materials");
    setTimeout(() => {
      const formElement = document.getElementById("material-form");
      if (formElement) {
        formElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  useEffect(() => {
    // Material tab'i aktifleştiğinde material.categories ve material.repairStyles değerlerini selectedCategories ve selectedRepairStyles'a aktar
    if (activeTab === "materials") {
      setSelectedCategories(material.categories);
      setSelectedRepairStyles(material.repairStyles);
      console.log("Material Tab aktif: Seçili kategoriler ve stiller güncellendi", {
        categories: material.categories,
        repairStyles: material.repairStyles
      });
    }
  }, [activeTab, material.categories, material.repairStyles]);

  // Delete dependent material
  const handleDeleteDependentMaterial = async (id) => {
    console.log("Attempting to delete dependent material with ID:", id);
    
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    
    if (result.isConfirmed) {
      setLoading(true);
      const api = axiosWithAuth();
      
      try {
        // Optimistically update UI
        setDependentMaterials(prev => prev.filter(item => {
          const itemId = typeof item.id === 'object' ? item.id.$oid || item.id._id || item.id : item.id;
          return itemId !== id;
        }));

        // Send delete request
        const response = await api.delete(`/dependent-materials/${id}`);
        
        if (response.status === 204) {
          // Show success message
          await Swal.fire({
            title: "Deleted!",
            text: "Dependent material has been deleted.",
            icon: "success",
            timer: 2000,
          });

          // Wait a moment then refresh to ensure we have latest data
          await new Promise(resolve => setTimeout(resolve, 500));
          await fetchAllDependentMaterials();
        }
      } catch (error) {
        console.error("Failed to delete dependent material:", error);
        
        // Revert optimistic update and refresh data
        fetchAllDependentMaterials();
        
        Swal.fire({
          title: "Error",
          text: "Failed to delete the material. Please try again.",
          icon: "error",
          timer: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Delete main material
  const handleDeleteMaterial = async (id) => {
    // Normalize the ID format
    const normalizedId = typeof id === 'object' ? (id.$oid || id._id || id.id) : id;
    console.log("Attempting to delete material with ID:", normalizedId);
    
    if (!normalizedId) {
      console.error("Invalid material ID:", id);
      Swal.fire({
        title: "Error",
        text: "Invalid material ID",
        icon: "error",
        timer: 2000,
      });
      return;
    }
    
    // Ask for confirmation
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    
    if (result.isConfirmed) {
      setLoading(true);
      const api = axiosWithAuth();
      
      try {
        // Check if this is a dependent material or main material based on the current tab
        const endpoint = activeTab === "dependentMaterials" ? 
          `/dependent-materials/${normalizedId}` : 
          `/materials/${normalizedId}`;
        
        console.log("Making DELETE request to:", endpoint);
        const response = await api.delete(endpoint);
        console.log("Delete response:", response);
        
        // Check if deletion was successful (204 No Content is success)
        if (response.status === 204 || response.status === 200) {
          // First update the local state immediately
          if (activeTab === "dependentMaterials") {
            setDependentMaterials(prev => {
              const filtered = prev.filter(item => {
                const itemId = typeof item.id === 'object' ? (item.id.$oid || item.id._id || item.id) : item.id;
                return itemId !== normalizedId;
              });
              console.log("Filtered dependent materials:", filtered);
              return filtered;
            });
            
            // Refresh the list after a short delay
            setTimeout(() => {
              console.log("Refreshing dependent materials...");
              fetchAllDependentMaterials();
            }, 500);
          } else {
            setMaterials(prev => {
              const filtered = prev.filter(item => {
                const itemId = typeof item.id === 'object' ? (item.id.$oid || item.id._id || item.id) : item.id;
                return itemId !== normalizedId;
              });
              console.log("Filtered materials:", filtered);
              return filtered;
            });
            
            // Refresh the list after a short delay
            setTimeout(() => {
              console.log("Refreshing materials...");
              fetchAllMaterials();
            }, 500);
          }
          
          // Show success message
          Swal.fire({
            title: "Deleted!",
            text: "Material has been deleted successfully.",
            icon: "success",
            timer: 2000,
          });
        } else {
          throw new Error(`Unexpected response status: ${response.status}`);
        }
      } catch (error) {
        console.error("Failed to delete material:", error);
        console.error("Error response:", error.response?.data);
        console.error("Error status:", error.response?.status);
        
        let errorMessage = "There was a problem deleting the material.";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        Swal.fire({
          title: "Error",
          text: errorMessage,
          icon: "error",
          timer: 3000,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // MongoDB objelerini frontend formatına dönüştüren yardımcı fonksiyon
  const convertMongoObjectToFrontendFormat = (item) => {
    // Yeni bir obje olarak kopyalayalım, orijinali değiştirmeyelim
    const result = { ...item };
    
    // _id dönüşümü
    if (result._id && typeof result._id === 'object' && result._id.$oid) {
      result.id = result._id.$oid;
    }
    
    // Diğer alanların dönüşümü
    // Diziler için recursive kontrol
    Object.keys(result).forEach(key => {
      const value = result[key];
      
      // MongoDB ObjectId formatı ($oid içeren objeler)
      if (value && typeof value === 'object' && value.$oid) {
        result[key] = value.$oid;
      } 
      // Diziler için recursive işlem
      else if (Array.isArray(value)) {
        result[key] = value.map(item => {
          if (item && typeof item === 'object') {
            if (item.$oid) {
              return item.$oid;
            } else {
              return convertMongoObjectToFrontendFormat(item);
            }
          }
          return item;
        });
      }
      // Nested objeler için recursive işlem
      else if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0) {
        result[key] = convertMongoObjectToFrontendFormat(value);
      }
    });
    
    return result;
  };

  // Default imaj için basit bir placeholder imaj URL'si kullan
  const getDefaultImage = () => {
    return "https://placehold.co/200x200/e9ecef/495057";
  };

  // Dosya URL'ini API'den gelen bilgilere göre elde et
  const getFileDownloadUrl = (imageData) => {
    if (!imageData) return getDefaultImage();

    // 1. id veya _id varsa, backend endpointini kullan
    const id = imageData._id || imageData.id;
    if (id) {
      return getApiEndpoint(`mono-files/download/${id}`);
    }

    // 2. path varsa, dosya adını çekip endpointi kullan
    if (imageData.path) {
      const fileName = imageData.path.split('/').pop();
      return getApiEndpoint(`mono-files/download/${fileName}`);
    }

    // 3. url varsa, dosya adını çekip endpointi kullan
    if (imageData.url) {
      const fileName = imageData.url.split('/').pop();
      return getApiEndpoint(`mono-files/download/${fileName}`);
    }

    // fallback
    if (typeof imageData === 'string') return imageData;
    if (imageData.hashBase64) return `data:image/png;base64,${imageData.hashBase64}`;
    return getDefaultImage();
  };
  
  // Görüntüleme ve bağlantı oluşturma için kullanılacak URL
  const getMonoFileViewUrl = (imageData) => {
    if (!imageData) return null;
    
    const id = imageData._id || imageData.id;
    if (!id) return null;
    
    return getApiEndpoint(`mono-files/${id}`);
  };

  const CategorySelector = ({ selectedCategories, onChange }) => {
    const handleCategoryChange = (e) => {
      const categoryId = e.target.value;
      if (!categoryId) return;
      
      // Add categoryId directly as a string
      if (!selectedCategories.includes(categoryId)) {
        onChange([...selectedCategories, categoryId]);
      }
    };

    const handleRemoveCategory = (categoryId) => {
      onChange(selectedCategories.filter(id => id !== categoryId));
    };

    return (
      <div className="material-category-selector">
        <div>
          <label className="form-label">Select Category</label>
          <select className="mb-2 form-control" onChange={handleCategoryChange} value="">
            <option value="">-- Select Category --</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name?.az || category.name?.en || ''}
              </option>
            ))}
          </select>
        </div>
        {selectedCategories && selectedCategories.length > 0 && (
          <div className="selected-category mt-2">
            <div className="list-group">
              {selectedCategories.map(categoryId => {
                // Find the full category data from available categories
                const category = categories.find(c => c.id === categoryId);
                return (
                  <div key={categoryId} className="list-group-item d-flex justify-content-between align-items-center">
                    {category?.name?.az || category?.name?.en || 'Unknown Category'}
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleRemoveCategory(categoryId)}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Update the form component to use the new CategorySelector
  const DependentMaterialForm = ({ initialData, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      name: initialData?.name || { az: '', en: '', ru: '' },
      description: initialData?.description || { az: '', en: '', ru: '' },
      categories: initialData?.categories || [],
      // ... rest of the form data ...
    });

    const handleCategoriesChange = (newCategories) => {
      setFormData(prev => ({
        ...prev,
        categories: newCategories
      }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <form onSubmit={handleSubmit}>
        {/* ... other form fields ... */}
        <div className="mb-3">
          <CategorySelector
            selectedCategories={formData.categories}
            onChange={handleCategoriesChange}
          />
        </div>
        {/* ... rest of the form ... */}
      </form>
    );
  };

  // Add these functions after the toggleDependentMaterial function

  // Toggle linked dependent material selection
  const toggleLinkedDependentMaterial = (depMaterial) => {
    console.log("Toggling linked dependent material:", depMaterial);
    
    // Normalize the material ID we're trying to toggle
    const depMaterialId = typeof depMaterial.id === 'object' 
      ? depMaterial.id.$oid || depMaterial.id._id || depMaterial.id 
      : depMaterial.id;
    
    console.log("Normalized material ID:", depMaterialId);
    
    setDependentMaterial(prev => {
      // Debug log the previous state
      console.log("Previous dependentMaterial state:", prev);
      
      const currentLinkedMaterials = Array.isArray(prev.linkedDependentMaterials) 
        ? prev.linkedDependentMaterials 
        : [];
      
      console.log("Current linked materials:", currentLinkedMaterials);
      
      // Check if already selected
      const isSelected = currentLinkedMaterials.some(item => {
        const linkedId = item?.material;
        console.log("Comparing linkedId:", linkedId, "with depMaterialId:", depMaterialId);
        return linkedId === depMaterialId;
      });
      
      let updatedLinkedMaterials;
      if (isSelected) {
        // Remove if selected
        updatedLinkedMaterials = currentLinkedMaterials.filter(item => item.material !== depMaterialId);
      } else {
        // Add if not selected
        updatedLinkedMaterials = [
          ...currentLinkedMaterials,
          {
            material: depMaterialId,
            quantity: 1
          }
        ];
      }
      
      console.log("Updated linked materials:", updatedLinkedMaterials);
      
      // Return new state with updated materials
      const newState = {
        ...prev,
        linkedDependentMaterials: updatedLinkedMaterials
      };
      
      console.log("New dependentMaterial state:", newState);
      return newState;
    });
  };

  // Handle quantity change for linked materials
  const handleLinkedQuantityChange = (materialId, quantity) => {
    setDependentMaterial(prev => ({
      ...prev,
      linkedDependentMaterials: prev.linkedDependentMaterials.map(dm =>
        dm.material === materialId ? { ...dm, quantity } : dm
      )
    }));
  };

  // Update isSelected check in the render section
  const isSelected = (item) => {
    if (!dependentMaterial?.linkedDependentMaterials?.length) {
      console.log("No linked materials found");
      return false;
    }
    
    const itemId = typeof item.id === 'object' 
      ? item.id.$oid || item.id._id || item.id 
      : item.id;
    
    console.log("Checking selection for item:", {
      itemId,
      itemName: item.name?.text || item.name?.az,
      linkedMaterials: dependentMaterial.linkedDependentMaterials
    });
    
    const isItemSelected = dependentMaterial.linkedDependentMaterials.some(link => {
      // Debug log each link object
      console.log("Processing link object:", link);
      
      // Get the material ID from either the material field or direct id
      const currentLinkedId = link.material || link.id;
      
      console.log("Comparing IDs:", {
        linkedId: currentLinkedId,
        itemId,
        matches: currentLinkedId === itemId
      });
      
      return currentLinkedId === itemId;
    });

    console.log("Selection result:", {
      itemId,
      itemName: item.name?.text || item.name?.az,
      isSelected: isItemSelected
    });

    return isItemSelected;
  };

  return (
    <div className="material-and-dependent-manager">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <Tabs
              id="material-manager-tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
            >
              <Tab eventKey="categories" title="Categories">
                <MaterialCategoryManager />
              </Tab>
              <Tab eventKey="dependentMaterials" title="Dependent Materials">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card mb-4">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">
                          {editingDependentMaterial ? "Edit" : "Add"} Dependent Material
                        </h5>
                        {editingDependentMaterial && (
                          <button 
                            type="button" 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetDependentMaterialForm}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                      <div className="card-body">
                        <form onSubmit={handleDependentMaterialSubmit}>
                          {/* Multi-language naming and description */}
                          <div className="section-title">Name and Description</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Multi-language Information</h6>
                            </div>
                            <div className="card-body">
                          {["az", "en", "ru"].map((lang) => (
                            <div key={lang} className="row g-3 mb-3">
                              <div className="col-md-6">
                                <label className="form-label">Name ({lang})</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  value={dependentMaterial.name[lang]}
                                  onChange={(e) => handleMultiLangInput(e, setDependentMaterial, dependentMaterial)}
                                  name={lang}
                                  data-field="name"
                                  required
                                  placeholder={`Name ${lang}`}
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Description ({lang})</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  value={dependentMaterial.description[lang]}
                                  onChange={(e) => handleMultiLangInput(e, setDependentMaterial, dependentMaterial)}
                                  name={lang}
                                  data-field="description"
                                  required
                                  placeholder={`Description ${lang}`}
                                />
                              </div>
                            </div>
                          ))}
                            </div>
                          </div>

                          {/* Category, Repair Style and Store */}
                          <div className="section-title">Material Classification</div>
                          <div className="row g-3 mb-3">
                            <div className="col-lg-6">
                              <div className="card h-100">
                                <div className="card-header">
                                  <h6 className="mb-0">Category & Store</h6>
                                </div>
                                <div className="card-body">
                                  <div className="row mb-3">
                                    <div className="col-12">
                                      <CategorySelector
                                        selectedCategories={dependentMaterial.categories}
                                        onChange={(cats) => setDependentMaterial(prev => ({ ...prev, categories: cats }))}
                                      />
                            </div>
                                  </div>
                                  <div className="row">
                                    <div className="col-12">
                                      <MaterialStoreSelector
                                        stores={stores}
                                        selectedStores={dependentMaterial.stores}
                                        onChange={(storesList) => setDependentMaterial(prev => ({ ...prev, stores: storesList }))}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="card h-100">
                                <div className="card-header">
                                  <h6 className="mb-0">Repair Tariff</h6>
                                </div>
                                <div className="card-body">
                                  <div className="row">
                                    <div className="col-12">
                                      <MaterialRepairTariffSelector
                                        tariffs={repairTariffs}
                                        selectedTariffs={dependentMaterial.repairTariffs}
                                        onChange={(tariffs) => setDependentMaterial(prev => ({ ...prev, repairTariffs: tariffs }))}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Measurement Information */}
                          <div className="section-title">Measurement Information</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Units and Capacity</h6>
                            </div>
                            <div className="card-body">
                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label className="form-label">Unit of Measure</label>
                              <select
                                className="form-select"
                                value={dependentMaterial.unitOfMeasure}
                                onChange={(e) => handleNumericInput(e, setDependentMaterial, dependentMaterial)}
                                name="unitOfMeasure"
                                required
                              >
                                <option value="">Seçin</option>
                                <option value="kq">kq</option>
                                <option value="qram">qram</option>
                                <option value="m2">m²</option>
                                <option value="metr">metr</option>
                                <option value="ədəd">ədəd</option>
                                <option value="top">top</option>
                                <option value="kisə">kisə</option>
                                <option value="paçka">paçka</option>
                                <option value="litr">litr</option>
                                <option value="kv.m">kv.m</option>
                                <option value="kub">kub</option>
                                <option value="seksiya">seksiya</option>
                                <option value="rulon">rulon</option>
                              </select>
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Measure Capacity</label>
                              <input
                                className="form-control"
                                type="number"
                                value={dependentMaterial.measureCapacity}
                                onChange={(e) => handleNumericInput(e, setDependentMaterial, dependentMaterial)}
                                name="measureCapacity"
                                required
                                placeholder="e.g. 10"
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Usage Per Square Meter</label>
                              <input
                                className="form-control"
                                type="number"
                                value={dependentMaterial.usagePerSquareMeter}
                                onChange={(e) => handleNumericInput(e, setDependentMaterial, dependentMaterial)}
                                name="usagePerSquareMeter"
                                required
                                placeholder="e.g. 0.5"
                              />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Price Information */}
                          <div className="section-title">Price Information</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Pricing Details</h6>
                            </div>
                            <div className="card-body">
                          <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                  <label className="form-label">Price</label>
                              <input
                                className="form-control"
                                type="number"
                                    value={dependentMaterial.price}
                                onChange={(e) => handleNumericInput(e, setDependentMaterial, dependentMaterial)}
                                    name="price"
                                required
                                    placeholder="Single Price"
                              />
                                  <small className="form-text text-muted">
                                    Single price system - only this field will be used from now on
                                  </small>
                            </div>
                                <div className="col-md-4">
                              <label className="form-label">Service Price</label>
                              <input
                                className="form-control"
                                type="number"
                                value={dependentMaterial.servicePrice}
                                onChange={(e) => handleNumericInput(e, setDependentMaterial, dependentMaterial)}
                                name="servicePrice"
                                required
                                placeholder="Service"
                              />
                            </div>
                                <div className="col-md-4">
                              <label className="form-label">Currency</label>
                              <input
                                className="form-control"
                                type="text"
                                value={dependentMaterial.currency}
                                onChange={(e) => handleNumericInput(e, setDependentMaterial, dependentMaterial)}
                                name="currency"
                                required
                                placeholder="e.g. AZN"
                              />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Images */}
                          <div className="section-title">Images</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Material Visuals</h6>
                            </div>
                            <div className="card-body">
                          <div className="row g-3 mb-3">
                            <div className="col-md-6">
                              <label className="form-label">Material Image</label>
                              <input
                                className="form-control"
                                type="file"
                                onChange={(e) => handleImageUpload(e, "image", setDependentMaterial, dependentMaterial)}
                                accept="image/*"
                              />
                              {dependentMaterial.image?.id && <div className="mt-2 text-success">Image selected (ID: {dependentMaterial.image.id})</div>}
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Room Image</label>
                              <input
                                className="form-control"
                                type="file"
                                onChange={(e) => handleImageUpload(e, "imageOnRoom", setDependentMaterial, dependentMaterial)}
                                accept="image/*"
                              />
                              {dependentMaterial.imageOnRoom?.id && <div className="mt-2 text-success">Image selected (ID: {dependentMaterial.imageOnRoom.id})</div>}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Add this section before the Options section in the DependentMaterial form */}
                          <div className="section-title">Linked Dependent Materials (Optional)</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Additional Components</h6>
                            </div>
                            <div className="card-body">
                              <div className="alert alert-info">
                                <i className="bi bi-info-circle-fill me-2"></i>
                                <strong>This section is optional.</strong> You can select other dependent materials to link to this one.
                                These materials will be included when this dependent material is used.
                              </div>
                              
                              {dependentMaterials && dependentMaterials.length > 0 ? (
                                <div className="dependent-materials-grid">
                                  {dependentMaterials
                                    .filter(item => item.id !== dependentMaterialEditId) // Don't show current material
                                    .map(item => {
                                      const selected = isSelected(item);
                                      console.log("Rendering material:", {
                                        id: item.id,
                                        name: item.name?.text || item.name?.az,
                                        isSelected: selected
                                      });
                                      
                                      return (
                                        <div 
                                          key={item.id} 
                                          className={`dependent-material-card ${selected ? 'selected' : ''}`}
                                          onClick={() => toggleLinkedDependentMaterial(item)}
                                        >
                                          <div className="card-image">
                                            <img 
                                              src={getFileDownloadUrl(item.image)}
                                              alt={item.name?.text || item.name?.az || "Dependent Material"} 
                                              className="img-thumbnail"
                                              onError={(e) => { 
                                                e.target.onerror = null;
                                                e.target.src = getDefaultImage();
                                              }}
                                              data-material-id={item.id}
                                            />
                                          </div>
                                          <div className="card-body">
                                            <div className="d-flex align-items-center mb-2">
                                              <h6 className="card-title">{item.name?.text || item.name?.az || "Unnamed"}</h6>
                                              {selected && (
                                                <span className="ms-2 badge bg-success">Selected</span>
                                              )}
                                            </div>
                                            <div className="material-info">
                                              <div><span className="info-label">Unit:</span> {item.unitOfMeasure || "-"}</div>
                                              <div><span className="info-label">Price:</span> {item.price || item.midPrice || 0} {item.currency || "AZN"}</div>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              ) : (
                                <div className="text-center py-4">
                                  <p className="mb-0">No other dependent materials available to link.</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Selected Linked Materials Summary */}
                          {dependentMaterial.linkedDependentMaterials?.length > 0 && (
                            <div className="card mb-3">
                              <div className="card-header">
                                <h6 className="mb-0">Selected Linked Materials</h6>
                              </div>
                              <div className="card-body p-0">
                                <div className="table-responsive">
                                  <table className="table table-sm mb-0">
                                    <thead>
                                      <tr>
                                        <th>Material</th>
                                        <th>Unit</th>
                                        <th>Price</th>
                                        <th>Service Price</th>
                                        <th>Quantity</th>
                                        <th>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {dependentMaterial.linkedDependentMaterials.map(link => {
                                        const material = dependentMaterials.find(m => m.id === link.material);
                                        if (!material) return null;
                                        
                                        return (
                                          <tr key={material.id}>
                                            <td>{material.name?.text || material.name?.az || "Unnamed"}</td>
                                            <td>{material.unitOfMeasure || "-"}</td>
                                            <td>{material.price || material.midPrice || 0} {material.currency || "AZN"}</td>
                                            <td>{material.servicePrice || 0} {material.currency || "AZN"}</td>
                                            <td>
                                              <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                style={{ width: '80px' }}
                                                value={link.quantity || 1}
                                                min="1"
                                                onChange={(e) => handleLinkedQuantityChange(material.id, Math.max(1, parseInt(e.target.value) || 1))}
                                              />
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => toggleLinkedDependentMaterial(material)}
                                              >
                                                Remove
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Options */}
                          <div className="section-title">Options</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Material Settings</h6>
                            </div>
                            <div className="card-body">
                              {/* Default Material */}
                          <div className="form-check mb-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                                  checked={dependentMaterial.isDefault}
                              onChange={(e) => setDependentMaterial(prev => ({
                                ...prev,
                                    isDefault: e.target.checked,
                              }))}
                                  id="defaultCheckDependent"
                            />
                                <label className="form-check-label" htmlFor="defaultCheckDependent">
                                  Default Material
                            </label>
                                <small className="form-text text-muted d-block">
                                  If checked, this material will be automatically included in all calculations regardless of user selection.
                                </small>
                          </div>

                              {/* Active Status */}
                          <div className="form-check mb-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                                  checked={dependentMaterial.isActive}
                              onChange={(e) => setDependentMaterial(prev => ({
                                ...prev,
                                    isActive: e.target.checked,
                              }))}
                                  id="activeCheckDependent"
                            />
                                <label className="form-check-label" htmlFor="activeCheckDependent">
                                  Active
                            </label>
                            <small className="form-text text-muted d-block">
                                  If unchecked, this material will be hidden from user selection.
                            </small>
                              </div>
                            </div>
                          </div>

                          <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                            <button 
                              type="submit" 
                              className="btn btn-primary"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Loading...
                                </>
                              ) : (
                                editingDependentMaterial ? "Update" : "Add"
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dependent Materials Table */}
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title">Dependent Materials List</h5>
                  </div>
                  <div className="card-body">
                    {loading && !dependentMaterials.length ? (
                      <div className="text-center my-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading dependent materials...</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Name</th>
                              <th>Category</th>
                              <th>Repair Tariffs</th>
                              <th>Store</th>
                              <th>Unit</th>
                              <th>Price</th>
                              <th>Service Price</th>
                              <th>Image</th>
                              <th>Default</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dependentMaterials && dependentMaterials.length > 0 ? (
                              dependentMaterials.map((item) => {
                                return (
                                  <tr key={item.id}>
                                    <td>{item.name?.text || item.name?.az || "Unnamed"}</td>
                                    <td>
                                      {item.categories?.map(cat => 
                                        cat.name?.text || cat.name?.az || cat.name || '-'
                                      ).join(', ') || '-'}
                                    </td>
                                    <td>{item.repairTariffs?.map(tariff => tariff.name?.text || tariff.name?.az || tariff.name).join(', ') || '-'}</td>
                                    <td>{item.stores?.map(store => store.name?.text || store.name?.az || store.name).join(', ') || '-'}</td>
                                    <td>{item.unitOfMeasure || '-'}</td>
                                    <td>{item.price || item.midPrice || 0} {item.currency || 'AZN'}</td>
                                    <td>{item.servicePrice || 0} {item.currency || 'AZN'}</td>
                                    <td>
                                      <img 
                                        src={getFileDownloadUrl(item.image)}
                                        alt={item.name?.text || item.name?.az || "material image"}
                                        width="60"
                                        height="60" 
                                        className="img-thumbnail" 
                                        onError={(e) => { 
                                          e.target.onerror = null;
                                          e.target.src = getDefaultImage();
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <span className={`badge ${item.isDefault === true ? 'bg-success' : 'bg-secondary'}`}>
                                        {item.isDefault === true ? 'Yes' : 'No'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => handleEditDependentMaterial(item)}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => handleDeleteDependentMaterial(item.id)}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="11" className="text-center">
                                  No dependent materials found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </Tab>
              <Tab eventKey="materials" title="Materials">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="card mb-4">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">{editingMaterial ? "Edit" : "Add"} Material</h5>
                        {editingMaterial && (
                          <button 
                            type="button" 
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetMaterialForm}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                      <div className="card-body">
                        <form onSubmit={handleMaterialSubmit}>
                          {/* Name and Description */}
                          <div className="section-title">Name and Description</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Multi-language Information</h6>
                            </div>
                            <div className="card-body">
                          {["az", "en", "ru"].map((lang) => (
                            <div key={lang} className="row g-3 mb-3">
                              <div className="col-md-6">
                                <label className="form-label">Name ({lang})</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  value={material.name[lang]}
                                  onChange={(e) => handleMultiLangInput(e, setMaterial, material)}
                                  name={lang}
                                  data-field="name"
                                  required
                                  placeholder={`Name ${lang}`}
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="form-label">Description ({lang})</label>
                                <input
                                  className="form-control"
                                  type="text"
                                  value={material.description[lang]}
                                  onChange={(e) => handleMultiLangInput(e, setMaterial, material)}
                                  name={lang}
                                  data-field="description"
                                  required
                                  placeholder={`Description ${lang}`}
                                />
                              </div>
                            </div>
                          ))}
                            </div>
                          </div>

                          {/* Category, Repair Style and Store */}
                          <div className="section-title">Material Classification</div>
                          <div className="row g-3 mb-3">
                            <div className="col-lg-6">
                              <div className="card h-100">
                                <div className="card-header">
                                  <h6 className="mb-0">Category & Repair Style</h6>
                            </div>
                                <div className="card-body">
                                  <div className="row mb-3">
                                    <div className="col-12">
                                      <CategorySelector
                                        selectedCategories={material.categories}
                                        onChange={(cats) => {
                                          setMaterial(prev => ({ ...prev, categories: cats }));
                                          setSelectedCategories(cats);
                                        }}
                                      />
                            </div>
                                  </div>
                                  <div className="row">
                                    <div className="col-12">
                                      <MaterialRepairStyleSelector
                                        repairStyles={repairStyles}
                                        selectedRepairStyles={material.repairStyles}
                                        onChange={(styles) => {
                                          setMaterial(prev => ({ ...prev, repairStyles: styles }));
                                          setSelectedRepairStyles(styles);
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-lg-6">
                              <div className="card h-100">
                                <div className="card-header">
                                  <h6 className="mb-0">Store & Tariff</h6>
                                </div>
                                <div className="card-body">
                                  <div className="row mb-3">
                                    <div className="col-12">
                                      <MaterialStoreSelector
                                        stores={stores}
                                        selectedStores={material.stores}
                                        onChange={(storesList) => setMaterial(prev => ({ ...prev, stores: storesList }))}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col-12">
                                      <MaterialRepairTariffSelector
                                        tariffs={repairTariffs}
                                        selectedTariffs={material.repairTariffs}
                                        onChange={(tariffs) => setMaterial(prev => ({ ...prev, repairTariffs: tariffs }))}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Measurement Information */}
                          <div className="section-title">Measurement Information</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Units and Capacity</h6>
                            </div>
                            <div className="card-body">
                          <div className="row g-3 mb-3">
                            <div className="col-md-4">
                              <label className="form-label">Unit of Measure</label>
                              <select
                                className="form-select"
                                value={material.unitOfMeasure}
                                onChange={(e) => handleNumericInput(e, setMaterial, material)}
                                name="unitOfMeasure"
                                required
                              >
                                <option value="">Seçin</option>
                                <option value="kq">kq</option>
                                <option value="qram">qram</option>
                                <option value="m2">m²</option>
                                <option value="metr">metr</option>
                                <option value="ədəd">ədəd</option>
                                <option value="top">top</option>
                                <option value="kisə">kisə</option>
                                <option value="paçka">paçka</option>
                                <option value="litr">litr</option>
                                <option value="kv.m">kv.m</option>
                                <option value="kub">kub</option>
                                <option value="seksiya">seksiya</option>
                                <option value="rulon">rulon</option>
                              </select>
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Measure Capacity</label>
                              <input
                                className="form-control"
                                type="number"
                                value={material.measureCapacity}
                                onChange={(e) => handleNumericInput(e, setMaterial, material)}
                                name="measureCapacity"
                                required
                                placeholder="e.g. 10"
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Usage Per Square Meter</label>
                              <input
                                className="form-control"
                                type="number"
                                value={material.usagePerSquareMeter}
                                onChange={(e) => handleNumericInput(e, setMaterial, material)}
                                name="usagePerSquareMeter"
                                required
                                placeholder="e.g. 0.5"
                              />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Price Information */}
                          <div className="section-title">Price Information</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Pricing Details</h6>
                            </div>
                            <div className="card-body">
                          <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                  <label className="form-label">Price</label>
                              <input
                                className="form-control"
                                type="number"
                                    value={material.price}
                                onChange={(e) => handleNumericInput(e, setMaterial, material)}
                                    name="price"
                                required
                                    placeholder="Single Price"
                              />
                                  <small className="form-text text-muted">
                                    Single price system - only this field will be used from now on
                                  </small>
                            </div>
                                <div className="col-md-4">
                              <label className="form-label">Service Price</label>
                              <input
                                className="form-control"
                                type="number"
                                value={material.servicePrice}
                                onChange={(e) => handleNumericInput(e, setMaterial, material)}
                                name="servicePrice"
                                required
                                placeholder="Service"
                              />
                            </div>
                                <div className="col-md-4">
                              <label className="form-label">Currency</label>
                              <input
                                className="form-control"
                                type="text"
                                value={material.currency}
                                onChange={(e) => handleNumericInput(e, setMaterial, material)}
                                name="currency"
                                required
                                placeholder="e.g. AZN"
                              />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Dependent Materials Selection */}
                          <div className="section-title">Dependent Materials (Optional)</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Additional Components</h6>
                            </div>
                            <div className="card-body">
                              <div className="alert alert-info">
                                <i className="bi bi-info-circle-fill me-2"></i>
                                <strong>This section is optional.</strong> You can select dependent materials for the main material if needed. 
                                These materials represent optional components that may be used with the main material, but are not required.
                              </div>
                              
                              {dependentMaterials && dependentMaterials.length > 0 ? (
                                <div className="dependent-materials-grid">
                                  {dependentMaterials.map(item => {
                                    const isSelected = selectedDependentMaterials.some(dm => dm.id === item.id);
                                    
                                    return (
                                      <div 
                                        key={item.id} 
                                        className={`dependent-material-card ${isSelected ? 'selected' : ''}`}
                                        onClick={() => toggleDependentMaterial(item)}
                                      >
                                        <div className="card-image">
                                          <img 
                                            src={getFileDownloadUrl(item.image)}
                                            alt={item.name?.text || item.name?.az || "Dependent Material"} 
                                            className="img-thumbnail"
                                            onError={(e) => { 
                                              e.target.onerror = null;
                                              e.target.src = getDefaultImage();
                                            }}
                                            data-material-id={item.id}
                                          />
                                        </div>
                                        <div className="card-body">
                                          <div className="d-flex align-items-center mb-2">
                                            <h6 className="card-title">{item.name?.text || item.name?.az || "Unnamed"}</h6>
                                          </div>
                                          <div className="material-info">
                                            <div><span className="info-label">Unit:</span> {item.unitOfMeasure || "-"}</div>
                                            <div><span className="info-label">Price:</span> {item.price || item.midPrice || 0} {item.currency || "AZN"}</div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="text-center py-4">
                                  <p className="mb-0">No dependent materials added yet.</p>
                                  <p>First add materials in the "Dependent Materials" tab.</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Selected Dependent Materials Summary */}
                          {selectedDependentMaterials && selectedDependentMaterials.length > 0 && (
                            <div className="card mb-3">
                              <div className="card-header">
                                <h6 className="mb-0">Selected Dependent Materials</h6>
                              </div>
                              <div className="card-body p-0">
                                <div className="table-responsive">
                                  <table className="table table-sm mb-0">
                                    <thead>
                                      <tr>
                                        <th>Material</th>
                                        <th>Unit</th>
                                        <th>Price</th>
                                        <th>Service Price</th>
                                        <th>Quantity</th>
                                        <th>Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {selectedDependentMaterials.map(material => (
                                        <tr key={material.id}>
                                          <td>{material.name?.text || material.name?.az || "Unnamed"}</td>
                                          <td>{material.unitOfMeasure || "-"}</td>
                                          <td>{material.price || material.midPrice || 0} {material.currency || "AZN"}</td>
                                          <td>{material.servicePrice || 0} {material.currency || "AZN"}</td>
                                          <td>
                                            <input
                                              type="number"
                                              className="form-control form-control-sm"
                                              style={{ width: '80px' }}
                                              value={materialQuantities[material.id] || 1}
                                              min="1"
                                              onChange={(e) => handleQuantityChange(material.id, Math.max(1, parseInt(e.target.value) || 1))}
                                            />
                                          </td>
                                          <td>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger"
                                              onClick={() => toggleDependentMaterial(material)}
                                            >
                                              Remove
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Options */}
                          <div className="section-title">Options</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Material Settings</h6>
                            </div>
                            <div className="card-body">
                              {/* Default Material */}
                              <div className="form-check mb-3">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={material.isDefault}
                                  onChange={(e) => setMaterial(prev => ({
                                    ...prev,
                                    isDefault: e.target.checked,
                                  }))}
                                  id="defaultCheckMaterial"
                                />
                                <label className="form-check-label" htmlFor="defaultCheckMaterial">
                                  Default Material
                                </label>
                                <small className="form-text text-muted d-block">
                                  If checked, this material will be automatically included in all calculations regardless of user selection.
                                </small>
                              </div>
                              
                              {/* Active Status */}
                              <div className="form-check mb-3">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={material.isActive}
                                  onChange={(e) => setMaterial(prev => ({
                                    ...prev,
                                    isActive: e.target.checked,
                                  }))}
                                  id="activeCheckMaterial"
                                />
                                <label className="form-check-label" htmlFor="activeCheckMaterial">
                                  Active
                                </label>
                                <small className="form-text text-muted d-block">
                                  If unchecked, this material will be hidden from user selection.
                                </small>
                              </div>
                            </div>
                          </div>

                          {/* Images */}
                          <div className="section-title">Images</div>
                          <div className="card mb-3">
                            <div className="card-header">
                              <h6 className="mb-0">Material Visuals</h6>
                            </div>
                            <div className="card-body">
                          <div className="row g-3 mb-3">
                            <div className="col-md-6">
                              <label className="form-label">Material Image</label>
                              <input
                                className="form-control"
                                type="file"
                                onChange={(e) => handleImageUpload(e, "image", setMaterial, material)}
                                accept="image/*"
                              />
                              {material.image?.id && <div className="mt-2 text-success">Image selected (ID: {material.image.id})</div>}
                            </div>
                            <div className="col-md-6">
                              <label className="form-label">Room Image</label>
                              <input
                                className="form-control"
                                type="file"
                                onChange={(e) => handleImageUpload(e, "imageOnRoom", setMaterial, material)}
                                accept="image/*"
                              />
                              {material.imageOnRoom?.id && <div className="mt-2 text-success">Image selected (ID: {material.imageOnRoom.id})</div>}
                            </div>
                          </div>
                          </div>
                          </div>

                          <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                            <button 
                              type="submit" 
                              className="btn btn-primary"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Loading...
                                </>
                              ) : (
                                editingMaterial ? "Update" : "Add"
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Materials Table */}
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title">Materials List</h5>
                  </div>
                  <div className="card-body">
                    {loading && !materials.length ? (
                      <div className="text-center my-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-2">Loading materials...</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-striped table-bordered table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Name</th>
                              <th>Category</th>
                              <th>Repair Style</th>
                              <th>Repair Tariffs</th>
                              <th>Store</th>
                              <th>Unit</th>
                              <th>Price</th>
                              <th>Service Price</th>
                              <th>Image</th>
                              <th>Default</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials && materials.length > 0 ? (
                              materials.map((item) => {
                                return (
                                  <tr key={item.id}>
                                    <td>{item.name?.text || item.name?.az || "Unnamed"}</td>
                                    <td>
                                      {item.categories?.map(cat => 
                                        cat.name?.text || cat.name?.az || cat.name || '-'
                                      ).join(', ') || '-'}
                                    </td>
                                    <td>{item.repairStyles?.map(style => style.name?.text || style.name?.az || style.name).join(', ') || '-'}</td>
                                    <td>{item.repairTariffs?.map(tariff => tariff.name?.text || tariff.name?.az || tariff.name).join(', ') || '-'}</td>
                                    <td>{item.stores?.map(store => store.name?.text || store.name?.az || store.name).join(', ') || '-'}</td>
                                    <td>{item.unitOfMeasure || '-'}</td>
                                    <td>{item.price || item.midPrice || 0} {item.currency || 'AZN'}</td>
                                    <td>{item.servicePrice || 0} {item.currency || 'AZN'}</td>
                                    <td>
                                      <img 
                                        src={getFileDownloadUrl(item.image)}
                                        alt={item.name?.text || item.name?.az || "material image"}
                                        width="60"
                                        height="60" 
                                        className="img-thumbnail" 
                                        onError={(e) => { 
                                          e.target.onerror = null;
                                          e.target.src = getDefaultImage();
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <span className={`badge ${item.isDefault === true ? 'bg-success' : 'bg-secondary'}`}>
                                        {item.isDefault === true ? 'Yes' : 'No'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => handleEditMaterial(item)}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => handleDeleteMaterial(item.id)}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="11" className="text-center">
                                  No main materials found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialAndDependentManager; 